import {createModel} from '@rematch/core';

import {
  UrlPromotionPayload,
  UrlPromotionsType,
  createCommonPromotion,
  UrlPromotionBody,
  checkUrl,
  fetchPromotionById,
  updateUrlPromotion,
} from '~/services/hot-api/promotions';
import {getProfileByContacts} from '~/services/promotions';
import {getUrlInfo, UrlInfoResponse} from '~/services/hotsearch/promotions';
import {PromotionTextTypes} from '~/utils/constants';
import {filterNotExistsLocations} from '~/utils/promotions';
import {UrlPromotion} from '~/services/hot-api';

import type {RootModel} from '..';

export type UrlPromotionsState = {
  profile: any;
  error: boolean;

  // Values
  type: UrlPromotionsType | null;
  mainForm: {
    url: string;
    onlyfans: string;
  };
  content: {
    website: string;
    headline: string;
    description: string;
  };
  selectedCategories: number[];
  selectedLocations: number[];
  bids: {
    cpc: string;
    daily: string;
  };
  promotion: UrlPromotion | null;
};

const defaultState: UrlPromotionsState = {
  type: null,
  profile: null,
  error: false,
  mainForm: {
    url: '',
    onlyfans: '',
  },
  content: {
    website: '',
    headline: '',
    description: '',
  },
  selectedCategories: [],
  selectedLocations: [],
  bids: {
    cpc: '',
    daily: '',
  },
  promotion: null,
};

const getPromotionPayload = (state: UrlPromotionsState): UrlPromotionPayload => {
  const {selectedLocations, selectedCategories, bids, type, profile, content} = state;
  switch (type) {
    case UrlPromotionsType.Onlyfans: {
      return {
        promo: PromotionTextTypes.Url,
        geo: {
          city_ids: selectedLocations,
          county_ids: [],
          state_ids: [],
          country_ids: [],
        },
        category_ids: selectedCategories,
        bids: {
          price: parseFloat(bids.cpc),
          daily_limit: parseFloat(bids.daily),
        },
        details: {
          type: UrlPromotionsType.Onlyfans,
          url: profile.profile_url,
          onlyfans: profile.profile,
          headline: content.headline,
          description: content.description,
          site_id: profile.site_id,
          page_id: profile.page_id,
          domain_id: profile.domain_id,
        },
      };
    }
    case UrlPromotionsType.Url: {
      return {
        promo: PromotionTextTypes.Url,
        geo: {
          city_ids: selectedLocations,
          county_ids: [],
          state_ids: [],
          country_ids: [],
        },
        category_ids: selectedCategories,
        bids: {
          price: parseFloat(bids.cpc),
          daily_limit: parseFloat(bids.daily),
        },
        details: {
          type: UrlPromotionsType.Url,
          url: content.website,
          headline: content.headline,
          description: content.description,
          site_id: profile.site_id,
          page_id: profile.page_id,
          domain_id: profile.domain_id,
        },
      };
    }
    default: {
      throw new Error('url promo type is not defined');
    }
  }
};

export const urlPromotion = createModel<RootModel>()({
  state: defaultState,
  reducers: {
    setPayload: (state: UrlPromotionsState, payload: Partial<UrlPromotionsState>) => ({
      ...state,
      ...payload,
    }),
  },
  effects: (dispatch) => {
    return {
      resetMainStep: () => {
        dispatch.urlPromotion.setPayload({
          type: null,
          profile: null,
          content: {
            website: '',
            headline: '',
            description: '',
          },
          selectedCategories: [],
          selectedLocations: [],
        });
      },
      mainFormSubmit: async ({url, onlyfans}: {url: string; onlyfans: string}, state) => {
        // Process url input flow
        if (url) {
          const urlResponse = await checkUrl(url);

          const isSuccess = urlResponse.data?.status === 'OK';

          if (!isSuccess) {
            return {
              type: UrlPromotionsType.Url,
              isSuccess: false,
            };
          }

          const {data: urlInformation} = await getUrlInfo(url);

          const selectedLocations = filterNotExistsLocations(
            urlInformation.site_geos || [],
            state.promotions.flatLocations
          );

          const selectedCategories = urlInformation.text_ads_categories || [];

          dispatch.urlPromotion.setPayload({
            type: UrlPromotionsType.Url,
            profile: urlInformation,
            content: {
              website: url,
              headline: urlInformation.title || '',
              description: urlInformation.description || '',
            },
            selectedCategories,
            selectedLocations,
          });

          await dispatch.promotions.requestCategories({
            type: PromotionTextTypes.Url,
            cityIds: selectedCategories,
            entityType: urlInformation.type,
            extraCategoryIds: selectedCategories,
            url,
          });

          return {
            type: UrlPromotionsType.Url,
            response: urlInformation,
            isSuccess,
          };
        }

        // Process onlyfans input flow
        if (onlyfans) {
          const profileResponse = await getProfileByContacts({
            onlyfans,
          });

          const isSuccess = profileResponse.data?.profiles?.length > 0;
          const profile = profileResponse.data?.profiles[0];

          if (isSuccess) {
            dispatch.urlPromotion.setPayload({
              type: UrlPromotionsType.Onlyfans,
              profile,
              content: {
                website: profile.profile_url,
                headline: profile.title || '',
                description: profile.description || '',
              },
            });
          }

          return {
            type: UrlPromotionsType.Onlyfans,
            response: profileResponse.data,
            isSuccess,
          };
        }

        return {
          type: '',
          isSuccess: false,
          response: null,
        };
      },
      createPromotion: async (_payload: void, state) => {
        const promotionPayload = getPromotionPayload(state.urlPromotion);

        const result = await createCommonPromotion(promotionPayload);

        return result.data;
      },
      getPromotionById: async (id: string) => {
        try {
          const res = await fetchPromotionById(id);

          dispatch.urlPromotion.setPayload({
            promotion: res.data,
          });
        } catch (err) {
          console.error(err);
        }
      },
      updateUrlPromotion: async (data: any) => {
        const promotionBody: UrlPromotionBody = {
          id: data.id,
          details: {
            name: data.name,
            url: data.url,
            title: data.title,
            description: data.description,
          },
          bids: {
            price: parseFloat(data.bids.cpc),
            daily_limit: parseFloat(data.bids.daily),
          },
          geo: data.locations,
          category_ids: data.categories,
        };

        try {
          await updateUrlPromotion(promotionBody);
        } catch (err) {
          console.error(err);
        }
      },
      checkUrlChangePossibility: async ({url, isOnlyfans}: {url: string; isOnlyfans: boolean}) => {
        if (isOnlyfans) {
          const {data} = await getProfileByContacts({onlyfans: url});

          if (data.profiles?.length > 0) {
            return {
              success: true,
              message: 'URL is valid and can be updated.',
            };
          }

          return {
            success: false,
            message:
              "We couldn't find your profile. Please make sure that the username is set correctly.",
          };
        }

        const {data} = await getProfileByContacts({profile_url: url});
        if (data.check_url.notFound) {
          return {success: false, message: 'Ad URL you provided is not found'};
        }
        if (data.check_url.notAccessible) {
          return {success: false, message: 'Ad URL you provided is not accessible'};
        }
        if (data.check_url.redirectToExternal) {
          return {success: false, message: 'Ad URL you provided redirects to external site'};
        }

        if (data.check_url.success && (!data.profiles || data.profiles.length === 0)) {
          return {
            success: false,
            message:
              "Sorry, we haven't indexed your page yet. Once your page will be indexed you'll receive notification. This may take up to 24 hours.",
          };
        }

        return {
          success: true,
          message: 'URL is valid and can be updated.',
        };
      },
    };
  },
});
